import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DEFAULT_PAYMENT_QR_IMAGE_URL, adminApi, normalizeApiAssetPath, resolveApiAssetUrl } from "../api/client";
import { getEventParticipantLabels } from "../utils/eventParticipantLabels";
import { numberToIndianCurrencyWords } from "../utils/numberWords";
import {
  OptionItem,
  PaymentMilestoneForm,
  QuotationFormState,
  QuotationItemForm,
  QuotationNoteForm,
  QuotationSectionForm,
  QuotationTermForm,
  RecipientForm,
  ResourceListItem
} from "../types";

interface CatalogItemRecord extends ResourceListItem {
  id: number;
  category_id?: number | null;
  item_code: string;
  item_name: string;
  description?: string | null;
  default_unit?: string | null;
  default_dimension_text?: string | null;
  default_unit_rate?: number | string | null;
  pricing_mode: "manual" | "quantity_x_rate";
  is_active?: number | boolean;
}

interface CatalogItemDraft {
  categoryId: number | "";
  itemCode: string;
  itemName: string;
  description: string;
  defaultUnit: string;
  defaultDimensionText: string;
  defaultUnitRate: number | "";
  pricingMode: "manual" | "quantity_x_rate";
}

type FormFieldErrors = Record<string, string>;

type QuotationEventKind = "other" | "birthday" | "corporate" | "wedding_reception" | "reception" | "wedding";
const allCategoriesValue = "__all_categories__";

const quotationEventKinds: Array<{ label: string; value: QuotationEventKind }> = [
  { label: "Other", value: "other" },
  { label: "Birthday", value: "birthday" },
  { label: "Corporate Event", value: "corporate" },
  { label: "Wedding + Reception", value: "wedding_reception" },
  { label: "Reception", value: "reception" },
  { label: "Wedding", value: "wedding" }
];

const kindKeywords: Record<QuotationEventKind, string[]> = {
  other: [],
  wedding: ["wedding", "reception", "mandap", "marriage", "sangeet", "haldi", "mehendi", "floral", "bridal"],
  birthday: ["birthday", "balloon", "cake", "kids", "theme birthday", "naming"],
  corporate: ["corporate", "conference", "launch", "inauguration", "seminar", "branding", "stage", "truss", "sound"],
  wedding_reception: ["wedding", "reception", "mandap", "marriage", "sangeet", "haldi", "mehendi", "floral", "bridal"],
  reception: ["reception", "stage", "backdrop", "floral", "welcome", "photo booth"]
};

function createEmptyItem(): QuotationItemForm {
  return {
    itemCode: "",
    itemName: "",
    itemDescription: "",
    quantity: 1,
    unit: "",
    dimensionText: "",
    pricingMode: "manual",
    unitRate: 0,
    discountType: "none",
    discountValue: 0,
    taxRate: 0,
    isOptional: false,
    includeInTotal: true,
    isVisible: true,
    remarks: ""
  };
}

function createSectionCode(index: number) {
  return String(index + 1).padStart(2, "0");
}

function createEmptySection(sectionCode = ""): QuotationSectionForm {
  return {
    sectionCode,
    sectionTitle: "",
    sectionDescription: "",
    isVisible: true,
    isOptional: false,
    showSectionTotal: false,
    sectionNote: "",
    items: [createEmptyItem()]
  };
}

function createDefaultNotes(): QuotationNoteForm[] {
  return [
    {
      title: "Additional Costs",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "Diesel for all generators - charged at actual rates", isHighlighted: false },
        { pointText: "Food cost for security and valet drivers - extra", isHighlighted: false },
        { pointText: "Technical rider for artists (if not finalized) - extra", isHighlighted: false },
        { pointText: "Any items not listed here - charged separately", isHighlighted: false }
      ]
    },
    {
      title: "Setup Requirements",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "Venue access required minimum 2 days prior to event", isHighlighted: false },
        { pointText: "All decorative elements will be themed for consistency", isHighlighted: false },
        { pointText: "Complete setup within allotted access time", isHighlighted: false }
      ]
    },
    {
      title: "Client Responsibilities",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "All permits and permissions (fireworks, sound, parking, venue)", isHighlighted: false },
        { pointText: "Weather contingency planning", isHighlighted: false },
        { pointText: "Final headcount confirmation before event", isHighlighted: false }
      ]
    },
    {
      title: "Decor Specification",
      icon: "",
      isVisible: true,
      points: [
        { pointText: "All floral decorations - 100% Artificial Flowers", isHighlighted: false },
        { pointText: "Consistent theme across all decor elements", isHighlighted: false },
        { pointText: "Premium quality artificial floral arrangements", isHighlighted: false }
      ]
    }
  ];
}

function createDefaultTerms(): QuotationTermForm[] {
  return [
    "Venue must be available 1 day prior for setup",
    "Event company not liable for permission-related issues",
    "Equipment damage due to weather charged extra",
    "Quantity / quality changes affect final billing",
    "Advance payment forfeited upon cancellation",
    "Early cancellation terms are negotiable",
    "Client receives digital copies of event setup documentation",
    "Client responsible for all required permissions and licenses",
    "Natural calamities (rain, storms) - company not responsible",
    "Items not in quotation will be charged extra",
    "Last-minute additions subject to availability",
    "Cancellation within 15 days = 75% of total amount charged",
    "Company reserves rights to use event photos for promotion",
    "This quotation is customizable per specific requirements and theme preferences"
  ].map((termText) => ({
    termText,
    isMandatory: true,
    isVisible: true
  }));
}

function createDefaultPaymentMilestones(): PaymentMilestoneForm[] {
  return [
    {
      milestoneLabel: "At Booking",
      percentage: 50,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Advance to confirm booking and reserve all services",
      paymentStatus: "pending",
      isVisible: true
    },
    {
      milestoneLabel: "1 Week Before Event",
      percentage: 30,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Second installment 7 days prior to event date",
      paymentStatus: "pending",
      isVisible: true
    },
    {
      milestoneLabel: "Day After Event",
      percentage: 20,
      dueRuleText: "",
      dueDate: "",
      amount: 0,
      description: "Final balance cleared the next day after event",
      paymentStatus: "pending",
      isVisible: true
    }
  ];
}

function createInitialState(): QuotationFormState {
  return {
    quotation: {
      organizationProfileId: undefined,
      customerId: undefined,
      eventId: undefined,
      templateId: undefined,
      quoteNumber: "",
      revisionNo: 0,
      status: "pending",
      pricingMode: "scope_only",
      title: "Event Quotation",
      subtitle: "",
      headerBrandName: "EVENTENA",
      headerTagline: "Crafted with care - Executed with excellence",
      footerBrandName: "EVENTENA",
      footerTagline: "Creating unforgettable moments with professional excellence",
      issuedByLabel: "Eventena",
      issueDate: new Date().toISOString().slice(0, 10),
      validUntil: "",
      currencyCode: "INR",
      showItemPrice: false,
      showItemAmount: false,
      showSectionTotals: false,
      showNotes: true,
      showPaymentSchedule: true,
      showTerms: true,
      themeTokens: {
        gold: "#C9A96E",
        gold_light: "#E8D5A3",
        gold_dark: "#8B6914"
      },
      headerConfig: {
        event_occasion: "",
        selected_service_category_id: ""
      },
      footerConfig: {
        footer_phone: "Mobile: +919405076050 +917972426493"
      },
      printConfig: {
        page_size: "A4",
        orientation: "portrait"
      },
      customerMessage: "",
      internalNotes: "",
      quoteDiscountTotal: 0,
      roundOffAmount: 0,
      grandTotalWords: "",
      totalNote: "Exclusive of diesel, food & extra permissions"
    },
    sections: [createEmptySection(createSectionCode(0))],
    adjustments: [],
    notes: createDefaultNotes(),
    terms: createDefaultTerms(),
    paymentMilestones: createDefaultPaymentMilestones(),
    recipients: []
  };
}

function createCatalogDraft(categoryId: number | "" = ""): CatalogItemDraft {
  return {
    categoryId,
    itemCode: "",
    itemName: "",
    description: "",
    defaultUnit: "",
    defaultDimensionText: "",
    defaultUnitRate: "",
    pricingMode: "manual"
  };
}

function textMatchesKind(value: unknown, kind: QuotationEventKind) {
  if (kind === "other") {
    return true;
  }

  const text = String(value || "").toLowerCase();
  return kindKeywords[kind].some((keyword) => text.includes(keyword));
}

function optionMatchesKind(option: OptionItem, kind: QuotationEventKind) {
  const raw = option.raw as Record<string, unknown> | undefined;
  return (
    textMatchesKind(option.label, kind) ||
    textMatchesKind(raw?.category_name, kind) ||
    textMatchesKind(raw?.category_code, kind) ||
    textMatchesKind(raw?.template_name, kind) ||
    textMatchesKind(raw?.item_name, kind) ||
    textMatchesKind(raw?.description, kind)
  );
}

function createFallbackSectionsForKind(kind: QuotationEventKind): QuotationSectionForm[] {
  const kindLabel = quotationEventKinds.find((item) => item.value === kind)?.label || "Event";

  return [
    {
      ...createEmptySection(createSectionCode(0)),
      sectionTitle: kind === "other" ? "Custom Event Services" : `${kindLabel} Services`
    }
  ];
}

function createEventSection(title: unknown, kind: QuotationEventKind): QuotationSectionForm[] {
  const kindLabel = quotationEventKinds.find((item) => item.value === kind)?.label || "Event";
  const sectionTitle = firstFilled(title, kind === "other" ? "Custom Event Services" : `${kindLabel} Services`);

  return [
    {
      ...createEmptySection(createSectionCode(0)),
      sectionTitle
    }
  ];
}

function buildCatalogOption(item: CatalogItemRecord): OptionItem {
  return {
    label: `${item.item_code} - ${item.item_name}`,
    value: item.id,
    raw: item
  };
}

function buildEventOption(item: ResourceListItem): OptionItem {
  return {
    label: String(item.title || item.event_code || `Event ${item.id || ""}`),
    value: Number(item.id),
    raw: item
  };
}

function applyCatalogItemToQuotationItem(item: QuotationItemForm, catalogItem: CatalogItemRecord): QuotationItemForm {
  return {
    ...item,
    catalogItemId: catalogItem.id || null,
    itemCode: catalogItem.item_code,
    itemName: catalogItem.item_name,
    itemDescription: catalogItem.description || "",
    unit: catalogItem.default_unit || "",
    dimensionText: catalogItem.default_dimension_text || "",
    pricingMode: catalogItem.pricing_mode,
    unitRate: Number(catalogItem.default_unit_rate || 0)
  };
}

function formatDateTimeInput(value?: string | null) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function formatDateInput(value?: string | null) {
  if (!value) {
    return "";
  }

  const raw = String(value).trim();

  if (!raw) {
    return "";
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const indianDateMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (indianDateMatch) {
    return `${indianDateMatch[3]}-${indianDateMatch[2]}-${indianDateMatch[1]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  return "";
}

function parseJsonField(value: unknown) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, string>;
    } catch (error) {
      return {};
    }
  }

  return value as Record<string, string>;
}

const defaultFooterPhone = "Mobile: +919405076050 +917972426493";

function normalizeFooterConfig(value: unknown) {
  const footerConfig = parseJsonField(value);
  const footerPhone = String(footerConfig.footer_phone || "").trim();

  if (!footerPhone || footerPhone.includes("90288") || footerPhone.includes("37837")) {
    return {
      ...footerConfig,
      footer_phone: defaultFooterPhone
    };
  }

  return footerConfig;
}

function normalizeEventenaLabel(value?: string | null, fallback = "Eventena") {
  const label = String(value || "").trim();
  const normalizedLabel = label.toLowerCase();
  const legacyLabel = ["ash", "vish events"].join("-");

  if (!label || normalizedLabel === legacyLabel) {
    return fallback;
  }

  return label;
}

function normalizeCategoryIdList(value: unknown) {
  const values = Array.isArray(value)
    ? value
    : typeof value === "string" && value.includes(",")
      ? value.split(",")
      : value !== undefined && value !== null && value !== ""
        ? [value]
        : [];

  return Array.from(new Set(values.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0)));
}

function firstFilled(...values: unknown[]) {
  const value = values.find((item) => String(item || "").trim());
  return value === undefined ? "" : String(value).trim();
}

function normalizeHeaderValue(value: unknown) {
  return firstFilled(value);
}

function isDefaultQuotationTitle(value: unknown) {
  const title = String(value || "").trim().toLowerCase();
  return !title || title === "event quotation" || quotationEventKinds.some((kind) => title === `${kind.label.toLowerCase()} event quotation`) || title === "wedding & reception event quotation";
}

function inferQuotationKindFromText(...values: unknown[]): QuotationEventKind {
  const text = values.map((value) => String(value || "").toLowerCase()).join(" ");

  if (text.includes("wedding") && text.includes("reception")) {
    return "wedding_reception";
  }

  if (text.includes("reception")) {
    return "reception";
  }

  if (text.includes("wedding")) {
    return "wedding";
  }

  if (text.includes("birthday")) {
    return "birthday";
  }

  if (text.includes("corporate")) {
    return "corporate";
  }

  return "other";
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read QR code image"));
    reader.readAsDataURL(file);
  });
}

function mapQuotationToState(payload: any): QuotationFormState {
  const headerConfig = parseJsonField(payload.header_config);
  const mappedNotes = (payload.notes || []).map((note: any) => ({
    templateNoteId: note.template_note_id,
    title: note.title,
    icon: note.icon || "",
    isVisible: Boolean(note.is_visible),
    points: (note.points || []).map((point: any) => ({
      templateNotePointId: point.template_note_point_id,
      pointText: point.point_text,
      isHighlighted: Boolean(point.is_highlighted)
    }))
  }));
  const mappedTerms = (payload.terms || []).map((term: any) => ({
    templateTermId: term.template_term_id,
    termText: term.term_text,
    isMandatory: Boolean(term.is_mandatory),
    isVisible: Boolean(term.is_visible)
  }));
  const mappedPaymentMilestones = (payload.paymentMilestones || []).map((milestone: any) => ({
    templatePaymentMilestoneId: milestone.template_payment_milestone_id,
    milestoneLabel: milestone.milestone_label,
    percentage: Number(milestone.percentage || 0),
    dueRuleText: milestone.due_rule_text || "",
    dueDate: formatDateInput(milestone.due_date),
    amount: Number(milestone.amount || 0),
    description: milestone.description || "",
    paymentStatus: milestone.payment_status || "pending",
    isVisible: Boolean(milestone.is_visible)
  }));

  return {
    quotation: {
      organizationProfileId: payload.organization_profile_id,
      customerId: payload.customer_id,
      eventId: payload.event_id,
      templateId: payload.template_id,
      quoteNumber: payload.quote_number,
      revisionNo: payload.revision_no,
      status: payload.status,
      pricingMode: payload.pricing_mode,
      title: payload.title,
      subtitle: payload.subtitle || "",
      headerBrandName: normalizeEventenaLabel(payload.header_brand_name, "EVENTENA"),
      headerTagline: payload.header_tagline || "",
      footerBrandName: normalizeEventenaLabel(payload.footer_brand_name, "EVENTENA"),
      footerTagline: payload.footer_tagline || "",
      issuedByLabel: normalizeEventenaLabel(payload.issued_by_label),
      issueDate: formatDateInput(payload.issue_date),
      validUntil: formatDateInput(payload.valid_until),
      currencyCode: payload.currency_code || "INR",
      showItemPrice: Boolean(payload.show_item_price),
      showItemAmount: Boolean(payload.show_item_amount),
      showSectionTotals: Boolean(payload.show_section_totals),
      showNotes: Boolean(payload.show_notes),
      showPaymentSchedule: Boolean(payload.show_payment_schedule),
      showTerms: Boolean(payload.show_terms),
      themeTokens: parseJsonField(payload.theme_tokens),
      headerConfig: {
        ...headerConfig,
        event_host_family_name: headerConfig.event_host_family_name || payload.event_host_family_name || payload.customer_name || "",
        event_groom_name: headerConfig.event_groom_name || payload.event_groom_name || "",
        event_bride_name: headerConfig.event_bride_name || payload.event_bride_name || "",
        event_start_at: formatDateInput(headerConfig.event_start_at || payload.event_start_at),
        event_venue: headerConfig.event_venue || payload.event_venue || "",
        event_occasion: headerConfig.event_occasion || payload.event_occasion || "",
        event_decor_type_label: headerConfig.event_decor_type_label || payload.event_decor_type_label || "",
        ...(payload.event_type_code ? { quotation_type_code: String(payload.event_type_code).toLowerCase() } : {}),
        ...(payload.event_type_name ? { quotation_type_label: payload.event_type_name } : {})
      },
      footerConfig: normalizeFooterConfig({
        ...parseJsonField(payload.footer_config),
        ...(payload.payment_qr_image_url ? { payment_qr_image_url: payload.payment_qr_image_url } : {})
      }),
      printConfig: parseJsonField(payload.print_config),
      customerMessage: payload.customer_message || "",
      internalNotes: payload.internal_notes || "",
      quoteDiscountTotal: Number(payload.quote_discount_total || 0),
      roundOffAmount: Number(payload.round_off_amount || 0),
      grandTotalWords: payload.grand_total_words || "",
      totalNote: payload.total_note || ""
    },
    sections: (payload.sections || []).map((section: any) => ({
      templateSectionId: section.template_section_id,
      serviceCategoryId: section.service_category_id,
      sectionCode: section.section_code,
      sectionTitle: section.section_title,
      sectionDescription: section.section_description || "",
      isVisible: Boolean(section.is_visible),
      isOptional: Boolean(section.is_optional),
      showSectionTotal: Boolean(section.show_section_total),
      sectionNote: section.section_note || "",
      items: (section.items || []).map((item: any) => ({
        templateItemId: item.template_item_id,
        catalogItemId: item.catalog_item_id,
        itemCode: item.item_code,
        itemName: item.item_name,
        itemDescription: item.item_description || "",
        quantity: Number(item.quantity || 0),
        unit: item.unit || "",
        dimensionText: item.dimension_text || "",
        pricingMode: item.pricing_mode,
        unitRate: Number(item.unit_rate || 0),
        discountType: item.discount_type,
        discountValue: Number(item.discount_value || 0),
        taxRate: Number(item.tax_rate || 0),
        isOptional: Boolean(item.is_optional),
        includeInTotal: Boolean(item.include_in_total),
        isVisible: Boolean(item.is_visible),
        remarks: item.remarks || ""
      }))
    })),
    adjustments: (payload.adjustments || []).map((adjustment: any) => ({
      adjustmentType: adjustment.adjustment_type,
      label: adjustment.label,
      calculationType: adjustment.calculation_type,
      calculationValue: Number(adjustment.calculation_value || 0),
      amount: Number(adjustment.amount || 0),
      isTaxable: Boolean(adjustment.is_taxable),
      isVisible: Boolean(adjustment.is_visible),
      remarks: adjustment.remarks || ""
    })),
    notes: mappedNotes.length ? mappedNotes : createDefaultNotes(),
    terms: mappedTerms.length ? mappedTerms : createDefaultTerms(),
    paymentMilestones: mappedPaymentMilestones.length ? mappedPaymentMilestones : createDefaultPaymentMilestones(),
    recipients: (payload.recipients || []).map((recipient: any) => ({
      customerContactId: recipient.customer_contact_id,
      userId: recipient.user_id,
      recipientName: recipient.recipient_name,
      recipientEmail: recipient.recipient_email || "",
      recipientPhone: recipient.recipient_phone || "",
      roleLabel: recipient.role_label || "",
      isPrimary: Boolean(recipient.is_primary),
      canView: Boolean(recipient.can_view),
      canRespond: Boolean(recipient.can_respond)
    }))
  };
}

function calculateItemLineTotal(item: QuotationItemForm) {
  const base = item.pricingMode === "quantity_x_rate" ? item.quantity * item.unitRate : item.unitRate;
  const discount = item.discountType === "percent" ? (base * item.discountValue) / 100 : item.discountType === "flat" ? item.discountValue : 0;
  const taxable = Math.max(base - discount, 0);
  const tax = (taxable * item.taxRate) / 100;
  return taxable + tax;
}

export default function QuotationEditorPage() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(quotationId);
  const [form, setForm] = useState<QuotationFormState>(createInitialState());
  const [expandedSection, setExpandedSection] = useState<number | false>(0);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FormFieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [organizationOptions, setOrganizationOptions] = useState<OptionItem[]>([]);
  const [customerOptions, setCustomerOptions] = useState<OptionItem[]>([]);
  const [eventOptions, setEventOptions] = useState<OptionItem[]>([]);
  const [eventTypeOptions, setEventTypeOptions] = useState<OptionItem[]>([]);
  const [templateOptions, setTemplateOptions] = useState<OptionItem[]>([]);
  const [contactOptions, setContactOptions] = useState<OptionItem[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<OptionItem[]>([]);
  const [catalogItemOptions, setCatalogItemOptions] = useState<OptionItem[]>([]);
  const [eventOptionsLoading, setEventOptionsLoading] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogDialogTarget, setCatalogDialogTarget] = useState<{ sectionIndex: number; itemIndex: number } | null>(null);
  const [catalogDraft, setCatalogDraft] = useState<CatalogItemDraft>(createCatalogDraft());
  const [creatingCatalogItem, setCreatingCatalogItem] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ title: string; onConfirm: () => void } | null>(null);
  const [pendingEventSwitch, setPendingEventSwitch] = useState<number | null>(null);
  const [prefillApplied, setPrefillApplied] = useState(false);
  const [masterOptionsLoaded, setMasterOptionsLoaded] = useState(false);
  const [categorySelectionTouched, setCategorySelectionTouched] = useState(false);
  const [isSwitchingEvent, startEventSwitchTransition] = useTransition();
  const sectionRebuildToken = useRef(0);
  const selectedQuotationType = form.quotation.headerConfig.quotation_type_code || form.quotation.headerConfig.quotation_event_kind || "";
  const selectedQuotationKind = inferQuotationKindFromText(selectedQuotationType, form.quotation.headerConfig.quotation_type_label, form.quotation.headerConfig.event_occasion);
  const eventParticipantLabels = getEventParticipantLabels(selectedQuotationKind);
  const filteredTemplateOptions = templateOptions.filter((option) => optionMatchesKind(option, selectedQuotationKind));
  const visibleTemplateOptions = filteredTemplateOptions.length ? filteredTemplateOptions : templateOptions;
  const selectedEventOption = form.quotation.eventId
    ? eventOptions.find((option) => Number(option.value) === Number(form.quotation.eventId))
    : undefined;
  const selectedEventRecord = (selectedEventOption?.raw || {}) as Record<string, unknown>;
  const selectedEventTypeOption = selectedEventOption
    ? eventTypeOptions.find((option) => Number(option.value) === Number(selectedEventRecord.event_type_id || 0))
    : undefined;
  const selectedQuotationTypeOption = selectedQuotationType
    ? eventTypeOptions.find((option) => String((option.raw as Record<string, unknown> | undefined)?.type_code || option.value).toLowerCase() === selectedQuotationType)
    : undefined;
  const selectedEventTypeId = selectedEventTypeOption?.value || selectedEventRecord.event_type_id || "";
  const selectedQuotationTypeEventTypeId = selectedQuotationTypeOption?.value || "";
  const categoryEventTypeId = selectedEventTypeId || selectedQuotationTypeEventTypeId;
  const headerSelectedCategoryIds = normalizeCategoryIdList(
    form.quotation.headerConfig.selected_service_category_ids ?? form.quotation.headerConfig.selected_service_category_id
  );
  const sectionSelectedCategoryIds = Array.from(new Set(form.sections.map((section) => Number(section.serviceCategoryId || 0)).filter((categoryId) => categoryId > 0)));
  const selectedQuotationCategoryIds = headerSelectedCategoryIds.length
    ? headerSelectedCategoryIds
    : categorySelectionTouched
      ? []
      : sectionSelectedCategoryIds;
  const selectedEventTypeLabel = String(
    (selectedEventTypeOption?.raw as Record<string, unknown> | undefined)?.type_name ||
    selectedEventTypeOption?.label ||
    (selectedQuotationTypeOption?.raw as Record<string, unknown> | undefined)?.type_name ||
    selectedQuotationTypeOption?.label ||
    ""
  );
  const mappedCategoryOptions = categoryEventTypeId
    ? categoryOptions.filter((option) => {
        const category = option.raw as Record<string, unknown> | undefined;
        const isActive = category?.is_active === undefined || category.is_active === true || category.is_active === 1;
        return isActive && Number(category?.event_type_id || 0) === Number(categoryEventTypeId);
      })
    : [];
  const visibleCategoryOptions = mappedCategoryOptions.length ? mappedCategoryOptions : categoryOptions;
  const selectedQuotationCategoryOptions = selectedQuotationCategoryIds
    .map((categoryId) => visibleCategoryOptions.find((option) => Number(option.value) === categoryId))
    .filter(Boolean) as OptionItem[];
  const selectedQuotationCategoryOption = selectedQuotationCategoryOptions[0];
  const quotationCategoryOptions = !selectedEventOption
    ? []
    : mappedCategoryOptions.length
      ? [
          ...selectedQuotationCategoryOptions.filter((selectedOption) => !mappedCategoryOptions.some((option) => Number(option.value) === Number(selectedOption.value))),
          ...mappedCategoryOptions
        ]
      : selectedQuotationCategoryOption
        ? selectedQuotationCategoryOptions
        : [];
  const hasEventWithoutMappedCategories = Boolean(form.quotation.eventId && selectedEventTypeId && !mappedCategoryOptions.length);
  const visibleEventOptions = form.quotation.customerId ? eventOptions : [];
  const selectedQuotationTypeInfo = selectedEventTypeLabel || form.quotation.headerConfig.quotation_type_label || "Select an event to view quotation type";
  const activeCatalogItemsByCategory = useMemo(() => {
    const byCategory = new Map<string, OptionItem[]>();
    const shared: OptionItem[] = [];

    catalogItemOptions.forEach((option) => {
      const item = option.raw as unknown as CatalogItemRecord;
      const isActive = item.is_active === undefined || item.is_active === true || item.is_active === 1;

      if (!isActive) {
        return;
      }

      if (!item.category_id) {
        shared.push(option);
        return;
      }

      const key = String(Number(item.category_id));
      byCategory.set(key, [...(byCategory.get(key) || []), option]);
    });

    return { byCategory, shared };
  }, [catalogItemOptions]);
  const getFieldError = (key: string) => fieldErrors[key] || "";
  const clearFieldError = (key: string) => {
    setFieldErrors((current) => {
      if (!current[key]) {
        return current;
      }

      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const summary = useMemo(() => {
    const subtotal = form.sections.reduce((sectionSum, section) => {
      if (!section.isVisible) {
        return sectionSum;
      }

      return sectionSum + section.items.reduce((itemSum, item) => itemSum + (item.includeInTotal && item.isVisible ? calculateItemLineTotal(item) : 0), 0);
    }, 0);
    const adjustments = form.adjustments.reduce((sum, adjustment) => sum + (adjustment.isVisible ? adjustment.amount : 0), 0);
    const total = subtotal + adjustments - form.quotation.quoteDiscountTotal + form.quotation.roundOffAmount;
    return { subtotal, adjustments, beforeQuoteAdjustments: subtotal + adjustments, total };
  }, [form.adjustments, form.quotation.quoteDiscountTotal, form.quotation.roundOffAmount, form.sections]);
  const grandTotalWords = useMemo(() => numberToIndianCurrencyWords(summary.total), [summary.total]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [organizations, customers, eventTypes, templates, categories] = await Promise.all([
          adminApi.listResource<any>("organization-profiles", "?limit=100"),
          adminApi.listResource<any>("customers", "?limit=200"),
          adminApi.listResource<any>("event-types", "?limit=200"),
          adminApi.listResource<any>("quotation-templates", "?limit=100"),
          adminApi.listResource<any>("service-categories", "?limit=200&sort=default_display_order&order=ASC")
        ]);

        const nextOrganizationOptions = organizations.items.map((item) => ({
          label: normalizeEventenaLabel(item.business_name),
          value: item.id,
          raw: item
        }));

        setOrganizationOptions(nextOrganizationOptions);
        if (!quotationId && nextOrganizationOptions.length) {
          setForm((current) => ({
            ...current,
            quotation: {
              ...current.quotation,
              organizationProfileId: current.quotation.organizationProfileId || Number(nextOrganizationOptions[0].value)
            }
          }));
        }
        setCustomerOptions(customers.items.map((item) => ({ label: item.display_name, value: item.id, raw: item })));
        const nextTemplateOptions = templates.items.map((item) => ({ label: item.template_name, value: item.id, raw: item }));
        const nextCategoryOptions = categories.items.map((item) => ({ label: item.category_name, value: item.id, raw: item }));

        setTemplateOptions(nextTemplateOptions);
        setEventTypeOptions(eventTypes.items.map((item) => ({ label: item.type_name, value: item.id, raw: item })));
        setCategoryOptions(nextCategoryOptions);
        setMasterOptionsLoaded(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load form options");
      }
    };

    void loadOptions();
  }, []);

  useEffect(() => {
    if (!masterOptionsLoaded) {
      return;
    }

    const loadDeferredOptions = async () => {
      try {
        const [contacts, catalogItems] = await Promise.all([
          adminApi.listResource<any>("customer-contacts", "?limit=200"),
          adminApi.listResource<any>("service-catalog-items", "?limit=500&sort=item_code&order=ASC")
        ]);

        setContactOptions(contacts.items.map((item) => ({ label: item.full_name, value: item.id, raw: item })));
        setCatalogItemOptions(catalogItems.items.map((item: CatalogItemRecord) => buildCatalogOption(item)));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load quotation item options");
      }
    };

    void loadDeferredOptions();
  }, [masterOptionsLoaded]);

  useEffect(() => {
    const controller = new AbortController();

    const loadQuotation = async () => {
      if (!quotationId) {
        return;
      }

      try {
        setLoading(true);
        const response = await adminApi.getWithSignal<any>(`/quotations/${quotationId}?view=editor`, controller.signal);
        setForm(mapQuotationToState(response));
        setExpandedSection(0);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }

        setError(err instanceof Error ? err.message : "Failed to load quotation");
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void loadQuotation();

    return () => {
      controller.abort();
    };
  }, [quotationId]);

  useEffect(() => {
    const customerId = Number(form.quotation.customerId || 0);

    if (!customerId) {
      setEventOptions([]);
      setEventOptionsLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadEventsForCustomer = async () => {
      try {
        setEventOptionsLoading(true);
        const response = await adminApi.getWithSignal<any>(
          `/admin/events?customer_id=${encodeURIComponent(String(customerId))}&limit=200&sort=event_start_at&order=DESC`,
          controller.signal
        );
        setEventOptions(response.items.map((item: ResourceListItem) => buildEventOption(item)));
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err.message : "Failed to load customer events");
        }
      } finally {
        if (!controller.signal.aborted) {
          setEventOptionsLoading(false);
        }
      }
    };

    void loadEventsForCustomer();

    return () => {
      controller.abort();
    };
  }, [form.quotation.customerId]);

  const loadTemplate = async () => {
    if (!form.quotation.templateId) {
      setError("Select a template first");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const templateId = form.quotation.templateId;
      const [sections, items, notes, notePoints, terms, milestones] = await Promise.all([
        adminApi.listResource<any>("quotation-template-sections", `?template_id=${templateId}&limit=100`),
        adminApi.listResource<any>("quotation-template-items", `?template_id=${templateId}&limit=500`),
        adminApi.listResource<any>("quotation-template-notes", `?template_id=${templateId}&limit=100`),
        adminApi.listResource<any>("quotation-template-note-points", "?limit=500"),
        adminApi.listResource<any>("quotation-template-terms", `?template_id=${templateId}&limit=200`),
        adminApi.listResource<any>("quotation-template-payment-milestones", `?template_id=${templateId}&limit=100`)
      ]);

      const sectionRows = sections.items;
      const itemRows = items.items;
      const noteRows = notes.items;
      const notePointRows = notePoints.items;

      if (!sectionRows.length) {
        setError("Selected template does not have service sections to apply");
        return;
      }

      setForm((current) => ({
        ...current,
        sections: sectionRows.map((section: any) => ({
          templateSectionId: section.id,
          serviceCategoryId: section.service_category_id,
          sectionCode: section.section_code,
          sectionTitle: section.section_title,
          sectionDescription: section.section_description || "",
          isVisible: Boolean(section.is_visible),
          isOptional: Boolean(section.is_optional),
          showSectionTotal: Boolean(section.show_section_total),
          sectionNote: "",
          items: itemRows
            .filter((item: any) => item.template_section_id === section.id)
            .sort((a: any, b: any) => Number(a.display_order) - Number(b.display_order))
            .map((item: any) => ({
              templateItemId: item.id,
              catalogItemId: item.catalog_item_id,
              itemCode: item.item_code,
              itemName: item.item_name,
              itemDescription: item.item_description || "",
              quantity: Number(item.default_quantity || 1),
              unit: item.default_unit || "",
              dimensionText: item.default_dimension_text || "",
              pricingMode: item.pricing_mode,
              unitRate: Number(item.default_unit_rate || 0),
              discountType: "none",
              discountValue: 0,
              taxRate: 0,
              isOptional: Boolean(item.is_optional),
              includeInTotal: true,
              isVisible: Boolean(item.is_visible),
              remarks: item.remarks || ""
            }))
        })),
        notes: noteRows.map((note: any) => ({
          templateNoteId: note.id,
          title: note.title,
          icon: note.icon || "",
          isVisible: Boolean(note.is_visible),
          points: notePointRows
            .filter((point: any) => point.template_note_id === note.id)
            .sort((a: any, b: any) => Number(a.display_order) - Number(b.display_order))
            .map((point: any) => ({
              templateNotePointId: point.id,
              pointText: point.point_text,
              isHighlighted: Boolean(point.is_highlighted)
            }))
        })),
        terms: terms.items.map((term: any) => ({
          templateTermId: term.id,
          termText: term.term_text,
          isMandatory: Boolean(term.is_mandatory),
          isVisible: Boolean(term.is_visible)
        })),
        paymentMilestones: milestones.items.map((milestone: any) => ({
          templatePaymentMilestoneId: milestone.id,
          milestoneLabel: milestone.milestone_label,
          percentage: Number(milestone.percentage || 0),
          dueRuleText: milestone.due_rule_text || "",
          dueDate: "",
          amount: 0,
          description: milestone.description || "",
          paymentStatus: "pending",
          isVisible: Boolean(milestone.is_visible)
        }))
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply template");
    } finally {
      setLoading(false);
    }
  };

  const handleQuotationChange = (key: keyof QuotationFormState["quotation"], value: any) => {
    clearFieldError(`quotation.${String(key)}`);
    setForm((current) => ({
      ...current,
      quotation: {
        ...current.quotation,
        [key]: value
      }
    }));
  };

  const handleGrandTotalChange = (value: number) => {
    const nextTotal = Number.isFinite(value) ? value : 0;
    const nextRoundOffAmount = Number((nextTotal - summary.beforeQuoteAdjustments + form.quotation.quoteDiscountTotal).toFixed(2));
    handleQuotationChange("roundOffAmount", nextRoundOffAmount);
  };

  const handleFooterConfigChange = (key: string, value: string) => {
    setForm((current) => ({
      ...current,
      quotation: {
        ...current.quotation,
        footerConfig: {
          ...current.quotation.footerConfig,
          [key]: value
        }
      }
    }));
  };

  const handleHeaderConfigChange = (key: string, value: string) => {
    setForm((current) => ({
      ...current,
      quotation: {
        ...current.quotation,
        headerConfig: {
          ...current.quotation.headerConfig,
          [key]: value
        }
      }
    }));
  };

  const buildCustomerPrintableDefaults = (customerId: number | null | undefined): Record<string, string> => {
    if (!customerId) {
      return {};
    }

    const selectedCustomer = customerOptions.find((option) => Number(option.value) === Number(customerId));
    const customer = (selectedCustomer?.raw || {}) as Record<string, unknown>;
    const customerName = firstFilled(customer.display_name, selectedCustomer?.label);
    const familyName = firstFilled(customer.family_name, customerName);

    return {
      event_host_family_name: normalizeHeaderValue(familyName),
      event_groom_name: normalizeHeaderValue(customerName)
    };
  };

  const buildItemsForCategory = (categoryId: number | string) => (
    [
      ...(activeCatalogItemsByCategory.byCategory.get(String(Number(categoryId))) || []),
      ...activeCatalogItemsByCategory.shared
    ]
      .map((option, itemIndex) => {
        const catalogItem = option.raw as unknown as CatalogItemRecord;
        return applyCatalogItemToQuotationItem(
          {
            ...createEmptyItem(),
            itemCode: createSectionCode(itemIndex)
          },
          catalogItem
        );
      })
  );

  const buildSectionsFromCategories = (matchingCategories: OptionItem[]) => (
    matchingCategories.map((categoryOption, sectionIndex) => {
      const category = categoryOption.raw as ResourceListItem | undefined;
      const matchingItems = buildItemsForCategory(categoryOption.value);

      return {
        ...createEmptySection(String(category?.category_code || createSectionCode(sectionIndex))),
        serviceCategoryId: Number(categoryOption.value),
        sectionTitle: String(category?.category_name || categoryOption.label),
        items: matchingItems
      };
    })
  );

  const normalizeMatchText = (value: unknown) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ");

  const getEventTypeForEvent = (eventRecord: Record<string, unknown>) => {
    const eventTypeId = Number(eventRecord.event_type_id || 0);
    return eventTypeOptions.find((option) => Number(option.value) === eventTypeId);
  };

  const findExactCategories = (eventTypeId?: unknown, ...values: unknown[]) => {
    const matchTexts = values.map(normalizeMatchText).filter(Boolean);
    const activeCategories = categoryOptions.filter((option) => {
      const category = option.raw as Record<string, unknown> | undefined;
      return category?.is_active === undefined || category.is_active === true || category.is_active === 1;
    });
    const matchedByEventType = eventTypeId
      ? activeCategories.filter((option) => Number((option.raw as Record<string, unknown> | undefined)?.event_type_id || 0) === Number(eventTypeId))
      : [];

    if (matchedByEventType.length) {
      return matchedByEventType;
    }

    if (!matchTexts.length) {
      return [];
    }

    return activeCategories.filter((option) => {
      const raw = option.raw as Record<string, unknown> | undefined;
      const categoryText = normalizeMatchText(raw?.category_name || option.label);
      const categoryCode = normalizeMatchText(raw?.category_code);

      return matchTexts.some((matchText) => categoryText === matchText || categoryCode === matchText);
    });
  };

  const filterCategoriesBySelection = (categories: OptionItem[], categoryIds?: number[] | number | null) => {
    const selectedIds = normalizeCategoryIdList(categoryIds);

    if (!selectedIds.length) {
      return categories;
    }

    return categories.filter((option) => selectedIds.includes(Number(option.value)));
  };

  const buildSectionsForEvent = (eventRecord: Record<string, unknown>, kind: QuotationEventKind, categoryIds?: number[] | number | null) => {
    const eventTitle = firstFilled(eventRecord.title, eventRecord.occasion_label);
    const exactCategories = findExactCategories(
      eventRecord.event_type_id,
      eventRecord.title,
      eventRecord.occasion_label,
      eventRecord.decor_type_label,
      eventRecord.theme_name
    );
    const filteredCategories = filterCategoriesBySelection(exactCategories, categoryIds);

    if (filteredCategories.length) {
      return buildSectionsFromCategories(filteredCategories);
    }

    return createEventSection(eventTitle, kind);
  };

  const scheduleSectionRebuild = (buildSections: () => QuotationSectionForm[], delayMs = 0) => {
    const token = sectionRebuildToken.current + 1;
    sectionRebuildToken.current = token;

    window.setTimeout(() => {
      if (sectionRebuildToken.current !== token) {
        return;
      }

      const sections = buildSections();

      startEventSwitchTransition(() => {
        if (sectionRebuildToken.current !== token) {
          return;
        }

        setForm((current) => ({
          ...current,
          sections
        }));
        setExpandedSection(0);
      });
    }, delayMs);
  };

  const handleQuotationCustomerChange = (customerId: number) => {
    clearFieldError("quotation.customerId");
    sectionRebuildToken.current += 1;
    setCategorySelectionTouched(false);

    setForm((current) => {
      const { quotation_event_kind, quotation_type_code, quotation_type_label, selected_service_category_id, selected_service_category_ids, ...restHeaderConfig } = current.quotation.headerConfig;

      return {
        ...current,
        quotation: {
          ...current.quotation,
          customerId,
          eventId: undefined,
          headerConfig: {
            ...restHeaderConfig,
            ...buildCustomerPrintableDefaults(customerId),
            event_bride_name: "",
            event_start_at: "",
            event_venue: "",
            event_occasion: "",
            event_decor_type_label: ""
          }
        }
      };
    });
  };

  const clearEventSelection = (rebuildSections: boolean) => {
    sectionRebuildToken.current += 1;
    setCategorySelectionTouched(false);

    setForm((current) => {
      const { quotation_event_kind, quotation_type_code, quotation_type_label, selected_service_category_id, selected_service_category_ids, event_start_at, event_venue, event_occasion, event_decor_type_label, event_bride_name, ...restHeaderConfig } = current.quotation.headerConfig;

      return {
        ...current,
        quotation: {
          ...current.quotation,
          eventId: undefined,
          templateId: undefined,
          headerConfig: restHeaderConfig
        },
        sections: rebuildSections ? [createEmptySection(createSectionCode(0))] : current.sections
      };
    });
    setExpandedSection(0);
  };

  const applyQuotationEventOptionChange = (selectedEvent: OptionItem, rebuildSections: boolean) => {
    setCategorySelectionTouched(false);

    const eventId = Number(selectedEvent.value);
    const eventRecord = (selectedEvent?.raw || {}) as Record<string, unknown>;
    const eventCustomerId = eventRecord.customer_id ? Number(eventRecord.customer_id) : undefined;
    const customerDefaults = buildCustomerPrintableDefaults(eventCustomerId || form.quotation.customerId);
    const eventTypeOption = getEventTypeForEvent(eventRecord);
    const eventType = (eventTypeOption?.raw || {}) as Record<string, unknown>;
    const eventTypeLabel = firstFilled(eventType.type_name, eventTypeOption?.label, eventRecord.event_type_name);
    const eventTypeCode = firstFilled(eventType.type_code, eventTypeOption?.value, eventTypeLabel).toLowerCase();
    const inferredKind = inferQuotationKindFromText(eventTypeLabel, eventTypeCode, eventRecord.occasion_label, eventRecord.title);
    const eventTitle = firstFilled(eventRecord.title, selectedEvent?.label);

    const nextTemplateId = templateOptions.find((option) => optionMatchesKind(option, inferredKind))?.value;

    setForm((current) => ({
      ...current,
      quotation: {
        ...current.quotation,
        customerId: eventCustomerId || current.quotation.customerId,
        eventId,
        templateId: nextTemplateId ? Number(nextTemplateId) : current.quotation.templateId,
        title: isDefaultQuotationTitle(current.quotation.title) && eventTitle ? `${eventTitle} Quotation` : current.quotation.title,
        headerConfig: {
          ...current.quotation.headerConfig,
          ...customerDefaults,
          quotation_event_kind: inferredKind,
          quotation_type_code: eventTypeCode || inferredKind,
          quotation_type_label: eventTypeLabel || eventTitle || quotationEventKinds.find((item) => item.value === inferredKind)?.label || "Other",
          selected_service_category_id: "",
          selected_service_category_ids: "",
          event_host_family_name: normalizeHeaderValue(firstFilled(eventRecord.host_family_name, customerDefaults.event_host_family_name)),
          event_groom_name: normalizeHeaderValue(firstFilled(eventRecord.groom_name, customerDefaults.event_groom_name)),
          event_bride_name: normalizeHeaderValue(eventRecord.bride_name),
          event_start_at: formatDateInput(firstFilled(eventRecord.event_start_at)),
          event_venue: normalizeHeaderValue(eventRecord.venue_name),
          event_occasion: normalizeHeaderValue(eventRecord.occasion_label),
          event_decor_type_label: normalizeHeaderValue(eventRecord.decor_type_label)
        }
      }
    }));
    setExpandedSection(0);
    setError(null);

    if (rebuildSections) {
      scheduleSectionRebuild(() => buildSectionsForEvent(eventRecord, inferredKind), 250);
    } else {
      sectionRebuildToken.current += 1;
    }
  };

  const applyQuotationEventChange = (eventId: number, rebuildSections: boolean) => {
    if (!eventId) {
      clearEventSelection(rebuildSections);
      return;
    }

    const selectedEvent = eventOptions.find((option) => Number(option.value) === Number(eventId));
    if (!selectedEvent) {
      return;
    }

    applyQuotationEventOptionChange(selectedEvent, rebuildSections);
  };

  const hasServiceSectionContent = () => (
    form.sections.some((section) =>
      section.sectionTitle.trim() ||
      section.sectionCode.trim() ||
      section.items.some((item) => item.itemName.trim() || item.itemCode.trim() || Boolean(item.catalogItemId))
    )
  );

  const handleQuotationEventChange = (eventId: number) => {
    const currentEventId = Number(form.quotation.eventId || 0);
    const nextEventId = Number(eventId || 0);

    if (currentEventId === nextEventId) {
      return;
    }

    if (isEditMode && currentEventId && hasServiceSectionContent()) {
      setPendingEventSwitch(nextEventId);
      return;
    }

    applyQuotationEventChange(nextEventId, !isEditMode);
  };

  useEffect(() => {
    if (quotationId || prefillApplied || !masterOptionsLoaded || !customerOptions.length) {
      return;
    }

    const applyPrefill = async () => {
      const eventId = Number(searchParams.get("eventId") || 0);
      const customerId = Number(searchParams.get("customerId") || 0);

      if (eventId) {
        try {
          const eventRecord = await adminApi.getResourceById<ResourceListItem>("events", eventId);
          const eventOption = buildEventOption(eventRecord);
          setEventOptions((current) => (
            current.some((option) => Number(option.value) === eventId) ? current : [eventOption, ...current]
          ));
          applyQuotationEventOptionChange(eventOption, true);
          setPrefillApplied(true);
        } catch (err) {
          setError(err instanceof Error ? err.message : "Failed to load selected event");
        }
        return;
      }

      if (customerId && customerOptions.some((option) => Number(option.value) === customerId)) {
        handleQuotationCustomerChange(customerId);
        setPrefillApplied(true);
      }
    };

    void applyPrefill();
  }, [quotationId, prefillApplied, masterOptionsLoaded, searchParams, customerOptions]);

  const rebuildSectionsFromSelectedEvent = () => {
    if (!selectedEventOption && !selectedQuotationTypeOption) {
      setError("Select a quotation type or event before auto-building service sections");
      return;
    }

    const eventType = ((selectedEventTypeOption || selectedQuotationTypeOption)?.raw || {}) as Record<string, unknown>;
    const eventTypeLabel = firstFilled(eventType.type_name, selectedEventTypeOption?.label, selectedQuotationTypeOption?.label, selectedEventRecord.event_type_name);
    const eventTypeCode = firstFilled(eventType.type_code, selectedEventTypeOption?.value, selectedQuotationTypeOption?.value, eventTypeLabel).toLowerCase();
    const inferredKind = inferQuotationKindFromText(eventTypeLabel, eventTypeCode, selectedEventRecord.occasion_label, selectedEventRecord.title);
    const matchingCategories = selectedEventTypeOption || selectedQuotationTypeOption
      ? findExactCategories((selectedEventTypeOption || selectedQuotationTypeOption)?.value, eventTypeLabel, eventTypeCode)
      : [];

    setForm((current) => ({
      ...current,
      sections: selectedEventOption
        ? buildSectionsForEvent(selectedEventRecord, inferredKind, selectedQuotationCategoryIds)
        : matchingCategories.length
          ? buildSectionsFromCategories(filterCategoriesBySelection(matchingCategories, selectedQuotationCategoryIds))
          : current.sections
    }));
    setExpandedSection(0);
    setError(null);
  };

  const handlePaymentQrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file for the QR code");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const uploaded = await adminApi.post<{ imageUrl: string; path?: string; payment_qr_image_url?: string }>("/quotations/payment-qr-upload", {
        quotationId: isEditMode ? quotationId : undefined,
        filename: file.name,
        mimeType: file.type,
        dataUrl
      });

      handleFooterConfigChange("payment_qr_image_url", normalizeApiAssetPath(uploaded.payment_qr_image_url || uploaded.path || uploaded.imageUrl));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload QR code image");
    }
  };

  const requestRemove = (title: string, onConfirm: () => void) => {
    setConfirmRemove({ title, onConfirm });
  };

  const handleConfirmRemove = () => {
    confirmRemove?.onConfirm();
    setConfirmRemove(null);
  };

  const handleConfirmEventSwitch = () => {
    const nextEventId = pendingEventSwitch || 0;
    setPendingEventSwitch(null);
    applyQuotationEventChange(nextEventId, true);
  };

  const handleQuotationCategoryChange = (categoryIds: number[]) => {
    if (!selectedEventOption) {
      return;
    }

    setCategorySelectionTouched(true);

    const eventType = (selectedEventTypeOption?.raw || {}) as Record<string, unknown>;
    const eventTypeLabel = firstFilled(eventType.type_name, selectedEventTypeOption?.label, selectedEventRecord.event_type_name);
    const eventTypeCode = firstFilled(eventType.type_code, selectedEventTypeOption?.value, eventTypeLabel).toLowerCase();
    const inferredKind = inferQuotationKindFromText(eventTypeLabel, eventTypeCode, selectedEventRecord.occasion_label, selectedEventRecord.title);

    setForm((current) => ({
      ...current,
      quotation: {
        ...current.quotation,
        headerConfig: {
          ...current.quotation.headerConfig,
          selected_service_category_id: categoryIds[0] ? String(categoryIds[0]) : "",
          selected_service_category_ids: categoryIds.join(",")
        }
      }
    }));
    scheduleSectionRebuild(() => buildSectionsForEvent(selectedEventRecord, inferredKind, categoryIds));
    setExpandedSection(0);
    setError(null);
  };

  const handleCancelEventSwitch = () => {
    setPendingEventSwitch(null);
  };

  const updateSection = (index: number, nextSection: QuotationSectionForm) => {
    setForm((current) => {
      const sections = [...current.sections];
      sections[index] = nextSection;
      return { ...current, sections };
    });
  };

  const handleSectionCategoryChange = (sectionIndex: number, categoryId: number | null) => {
    const selectedCategory = categoryOptions.find((option) => Number(option.value) === Number(categoryId));
    const raw = selectedCategory?.raw as ResourceListItem | undefined;
    const section = form.sections[sectionIndex];
    const categoryItems = categoryId ? buildItemsForCategory(categoryId) : [];
    const hasOnlyBlankItems = !section.items.length || section.items.every((item) => !item.itemCode.trim() && !item.itemName.trim());

    updateSection(sectionIndex, {
      ...section,
      serviceCategoryId: categoryId,
      sectionCode: String(raw?.category_code || section.sectionCode || ""),
      sectionTitle: String(raw?.category_name || selectedCategory?.label || section.sectionTitle || ""),
      items: hasOnlyBlankItems && categoryItems.length ? categoryItems : section.items
    });
  };

  const addSection = () => {
    setForm((current) => ({
      ...current,
      sections: [...current.sections, createEmptySection(createSectionCode(current.sections.length))]
    }));
    setExpandedSection(form.sections.length);
  };

  const removeSection = (index: number) => {
    setForm((current) => ({
      ...current,
      sections: current.sections.filter((_, currentIndex) => currentIndex !== index)
    }));
    setExpandedSection((current) => {
      if (current === false) {
        return false;
      }

      if (current === index) {
        return index > 0 ? index - 1 : 0;
      }

      if (current > index) {
        return current - 1;
      }

      return current;
    });
  };

  const addItem = (sectionIndex: number) => {
    setForm((current) => {
      const sections = [...current.sections];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        items: [...sections[sectionIndex].items, createEmptyItem()]
      };
      return { ...current, sections };
    });
  };

  const removeItem = (sectionIndex: number, itemIndex: number) => {
    setForm((current) => {
      const sections = [...current.sections];
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        items: sections[sectionIndex].items.filter((_, currentIndex) => currentIndex !== itemIndex)
      };
      return { ...current, sections };
    });
  };

  const updateItem = (sectionIndex: number, itemIndex: number, nextItem: QuotationItemForm) => {
    setForm((current) => {
      const sections = [...current.sections];
      const section = sections[sectionIndex];
      const items = [...section.items];
      items[itemIndex] = nextItem;
      sections[sectionIndex] = {
        ...section,
        items
      };
      return { ...current, sections };
    });
  };

  const getCatalogOptionById = (catalogItemId?: number | null) => {
    if (!catalogItemId) {
      return null;
    }

    return catalogItemOptions.find((option) => Number(option.value) === Number(catalogItemId)) || null;
  };

  const getCatalogOptionsForSection = (section: QuotationSectionForm, currentItem?: QuotationItemForm) => {
    const filteredOptions = section.serviceCategoryId
      ? [
          ...(activeCatalogItemsByCategory.byCategory.get(String(Number(section.serviceCategoryId))) || []),
          ...activeCatalogItemsByCategory.shared
        ]
      : catalogItemOptions;

    const selectedOption = getCatalogOptionById(currentItem?.catalogItemId);

    if (selectedOption && !filteredOptions.some((option) => Number(option.value) === Number(selectedOption.value))) {
      return [selectedOption, ...filteredOptions];
    }

    return filteredOptions;
  };

  const handleCatalogItemSelect = (sectionIndex: number, itemIndex: number, option: OptionItem | null) => {
    if (!option) {
      updateItem(sectionIndex, itemIndex, {
        ...form.sections[sectionIndex].items[itemIndex],
        catalogItemId: null
      });
      return;
    }

    const catalogItem = option.raw as unknown as CatalogItemRecord;
    clearFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemCode`);
    clearFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemName`);

    setForm((current) => {
      const sections = [...current.sections];
      const section = sections[sectionIndex];
      const items = [...section.items];
      items[itemIndex] = applyCatalogItemToQuotationItem(items[itemIndex], catalogItem);
      sections[sectionIndex] = {
        ...section,
        serviceCategoryId: section.serviceCategoryId || catalogItem.category_id || null,
        items
      };
      return { ...current, sections };
    });
  };

  const openCatalogDialog = (sectionIndex: number, itemIndex: number) => {
    setCatalogDialogTarget({ sectionIndex, itemIndex });
    setCatalogDraft({
      ...createCatalogDraft(form.sections[sectionIndex]?.serviceCategoryId || "")
    });
    setCatalogDialogOpen(true);
  };

  const closeCatalogDialog = () => {
    setCatalogDialogOpen(false);
    setCatalogDialogTarget(null);
    setCatalogDraft(createCatalogDraft());
  };

  const createCatalogItem = async () => {
    if (!catalogDraft.categoryId || !catalogDraft.itemName.trim() || !catalogDraft.defaultDimensionText.trim()) {
      setError("Category, item name and dimension size are required");
      return;
    }

    try {
      setCreatingCatalogItem(true);
      setError(null);

      const created = await adminApi.createResource<CatalogItemRecord>("service-catalog-items", {
        category_id: Number(catalogDraft.categoryId),
        item_name: catalogDraft.itemName.trim(),
        description: catalogDraft.description.trim() || null,
        default_unit: catalogDraft.defaultUnit.trim() || null,
        default_dimension_text: catalogDraft.defaultDimensionText.trim() || null,
        default_unit_rate: catalogDraft.defaultUnitRate === "" ? null : Number(catalogDraft.defaultUnitRate),
        pricing_mode: catalogDraft.pricingMode,
        is_active: true
      });

      const nextOption = buildCatalogOption(created);

      setCatalogItemOptions((current) =>
        [...current, nextOption].sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }))
      );

      if (catalogDialogTarget) {
        handleCatalogItemSelect(catalogDialogTarget.sectionIndex, catalogDialogTarget.itemIndex, nextOption);
      }

      closeCatalogDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create item");
    } finally {
      setCreatingCatalogItem(false);
    }
  };

  const validateQuotation = () => {
    const nextFieldErrors: FormFieldErrors = {};
    let firstError: string | null = null;
    let firstSectionErrorIndex: number | null = null;

    const addFieldError = (key: string, message: string, sectionIndex?: number) => {
      nextFieldErrors[key] = message;

      if (!firstError) {
        firstError = message;
      }

      if (firstSectionErrorIndex === null && sectionIndex !== undefined) {
        firstSectionErrorIndex = sectionIndex;
      }
    };

    if (isEditMode && !form.quotation.quoteNumber.trim()) {
      addFieldError("quotation.quoteNumber", "Quote number is required");
    }

    if (!form.quotation.organizationProfileId) {
      addFieldError("quotation.organizationProfileId", "Organization is required");
    }

    if (!form.quotation.customerId) {
      addFieldError("quotation.customerId", "Customer is required");
    }

    if (!form.quotation.title.trim()) {
      addFieldError("quotation.title", "Title is required");
    }

    if (!form.sections.length) {
      firstError = firstError || "At least one service section is required";
    }

    if (form.quotation.quoteDiscountTotal < 0) {
      addFieldError("quotation.quoteDiscountTotal", "Quote discount cannot be negative");
    }

    if (form.quotation.quoteDiscountTotal > summary.beforeQuoteAdjustments) {
      addFieldError("quotation.quoteDiscountTotal", "Quote discount cannot be greater than quotation total");
    }

    for (let sectionIndex = 0; sectionIndex < form.sections.length; sectionIndex += 1) {
      const section = form.sections[sectionIndex];

      if (!section.sectionCode.trim()) {
        addFieldError(`sections.${sectionIndex}.sectionCode`, `Section ${sectionIndex + 1} needs a code`, sectionIndex);
      }

      if (!section.sectionTitle.trim()) {
        addFieldError(`sections.${sectionIndex}.sectionTitle`, `Section ${sectionIndex + 1} needs a title`, sectionIndex);
      }

      for (let itemIndex = 0; itemIndex < section.items.length; itemIndex += 1) {
        const item = section.items[itemIndex];

        if (!item.itemCode.trim()) {
          addFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemCode`, `Item ${itemIndex + 1} needs a code`, sectionIndex);
        }

        if (!item.itemName.trim()) {
          addFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemName`, `Item ${itemIndex + 1} needs a name`, sectionIndex);
        }
      }
    }

    setFieldErrors(nextFieldErrors);

    if (firstSectionErrorIndex !== null) {
      setExpandedSection(firstSectionErrorIndex);
    }

    return firstError;
  };

  const saveQuotation = async () => {
    try {
      const validationError = validateQuotation();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSaving(true);
      setError(null);
      const paymentQrImageUrl = normalizeApiAssetPath(form.quotation.footerConfig.payment_qr_image_url);
      const payload = {
        quotation: {
          ...form.quotation,
          payment_qr_image_url: paymentQrImageUrl || null,
          footerConfig: {
            ...form.quotation.footerConfig,
            ...(paymentQrImageUrl ? { payment_qr_image_url: paymentQrImageUrl } : {})
          },
          grandTotalWords
        },
        sections: form.sections,
        adjustments: form.adjustments,
        notes: form.notes,
        terms: form.terms,
        paymentMilestones: form.paymentMilestones.map((milestone) => ({
          ...milestone,
          amount: milestone.amount || Number(((summary.total * milestone.percentage) / 100).toFixed(2))
        })),
        recipients: form.recipients
      };

      if (isEditMode && quotationId) {
        await adminApi.put(`/quotations/${quotationId}`, payload);
        navigate(`/admin/quotations/${quotationId}/print`);
      } else {
        const createdQuotation = await adminApi.post<{ id?: number }>("/quotations", payload);
        navigate(`/admin/quotations/${createdQuotation.id}/print`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Stack spacing={3} sx={{ pb: { xs: 14, sm: 12 } }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", lg: "center" }}>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              {isEditMode ? "Edit quotation" : "Create quotation"}
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              Build a premium quotation with nested service sections, printable proposal styling, and customer response workflow.
            </Typography>
          </div>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "stretch", lg: "flex-end" }}
            sx={{ width: { xs: "100%", lg: "auto" } }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate("/admin/quotations")}
              sx={{ minHeight: 44, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={() => void loadTemplate()}
              sx={{ minHeight: 44, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              Apply template
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={() => void saveQuotation()}
              disabled={saving || (isEditMode && loading)}
              sx={{ minHeight: 44, px: 2.5, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              {saving ? "Saving..." : "Save quotation"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading && isEditMode ? <Alert severity="info">Loading quotation details...</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2.5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Quotation basics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Quote Number" value={form.quotation.quoteNumber} error={Boolean(getFieldError("quotation.quoteNumber"))} helperText={getFieldError("quotation.quoteNumber") || (!isEditMode ? "Generated automatically after save." : "")} onChange={(event) => handleQuotationChange("quoteNumber", event.target.value)} disabled={!isEditMode} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Organization" select value={form.quotation.organizationProfileId || ""} error={Boolean(getFieldError("quotation.organizationProfileId"))} helperText={getFieldError("quotation.organizationProfileId")} onChange={(event) => handleQuotationChange("organizationProfileId", Number(event.target.value))}>
                    {organizationOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Template" select value={form.quotation.templateId || ""} onChange={(event) => handleQuotationChange("templateId", Number(event.target.value))}>
                    {visibleTemplateOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Quotation Type" value={selectedQuotationTypeInfo} InputProps={{ readOnly: true }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Customer" select value={form.quotation.customerId || ""} error={Boolean(getFieldError("quotation.customerId"))} helperText={getFieldError("quotation.customerId")} onChange={(event) => handleQuotationCustomerChange(Number(event.target.value))}>
                    {customerOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Event"
                    select
                    value={form.quotation.eventId || ""}
                    onChange={(event) => handleQuotationEventChange(Number(event.target.value || 0))}
                    disabled={!form.quotation.customerId || eventOptionsLoading}
                    helperText={
                      !form.quotation.customerId
                        ? "Select customer first."
                        : eventOptionsLoading
                          ? "Loading events..."
                          : visibleEventOptions.length
                            ? ""
                            : "No events found for selected customer."
                    }
                  >
                    <MenuItem value="">Select event</MenuItem>
                    {visibleEventOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Category"
                    select
                    value={selectedQuotationCategoryIds}
                    onChange={(event) => {
                      const rawValue = event.target.value;
                      const values = Array.isArray(rawValue) ? rawValue : String(rawValue).split(",");

                      if (values.includes(allCategoriesValue)) {
                        handleQuotationCategoryChange([]);
                        return;
                      }

                      handleQuotationCategoryChange(normalizeCategoryIdList(values));
                    }}
                    disabled={!selectedEventOption || !quotationCategoryOptions.length}
                    SelectProps={{
                      multiple: true,
                      displayEmpty: true,
                      renderValue: (value) => {
                        const selectedIds = normalizeCategoryIdList(value);
                        const mappedIds = mappedCategoryOptions.map((option) => Number(option.value)).filter((categoryId) => categoryId > 0);
                        const isAllSelected = Boolean(mappedIds.length) && mappedIds.every((categoryId) => selectedIds.includes(categoryId));

                        if (!selectedIds.length || isAllSelected) {
                          return "All Categories";
                        }

                        const selectedOptions = selectedIds
                          .map((categoryId) => quotationCategoryOptions.find((option) => Number(option.value) === categoryId))
                          .filter(Boolean) as OptionItem[];

                        return selectedOptions.length ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selectedOptions.map((option) => (
                              <Chip
                                key={String(option.value)}
                                label={option.label}
                                size="small"
                                onMouseDown={(chipEvent) => chipEvent.stopPropagation()}
                                onDelete={() => {
                                  const nextIds = selectedIds.filter((categoryId) => Number(option.value) !== categoryId);
                                  handleQuotationCategoryChange(nextIds);
                                }}
                              />
                            ))}
                          </Box>
                        ) : "All Categories";
                      }
                    }}
                    helperText={
                      !selectedEventOption
                        ? "Select event first."
                        : quotationCategoryOptions.length
                          ? "Blank shows all event categories."
                          : "No categories linked to selected event."
                    }
                  >
                    <MenuItem value={allCategoriesValue}>All Categories</MenuItem>
                    {quotationCategoryOptions.map((option) => (
                      <MenuItem key={String(option.value)} value={option.value}>
                        <Checkbox size="small" checked={selectedQuotationCategoryIds.includes(Number(option.value))} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Title" value={form.quotation.title} error={Boolean(getFieldError("quotation.title"))} helperText={getFieldError("quotation.title")} onChange={(event) => handleQuotationChange("title", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Subtitle" value={form.quotation.subtitle || ""} onChange={(event) => handleQuotationChange("subtitle", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Issue Date" type="date" InputLabelProps={{ shrink: true }} value={form.quotation.issueDate || ""} onChange={(event) => handleQuotationChange("issueDate", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Valid Until" type="date" InputLabelProps={{ shrink: true }} value={form.quotation.validUntil || ""} onChange={(event) => handleQuotationChange("validUntil", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Status" select value={form.quotation.status} onChange={(event) => handleQuotationChange("status", event.target.value)}>
                    {["draft", "pending", "granted", "revision_requested", "rejected", "cancelled", "expired", "archived"].map((status) => (
                      <MenuItem key={status} value={status}>{status}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Pricing Mode" select value={form.quotation.pricingMode} onChange={(event) => handleQuotationChange("pricingMode", event.target.value)}>
                    <MenuItem value="scope_only">Scope Only</MenuItem>
                    <MenuItem value="detailed_pricing">Detailed Pricing</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Quote Discount" type="number" value={form.quotation.quoteDiscountTotal} error={Boolean(getFieldError("quotation.quoteDiscountTotal"))} helperText={getFieldError("quotation.quoteDiscountTotal")} onChange={(event) => handleQuotationChange("quoteDiscountTotal", Number(event.target.value))} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Round Off Amount" type="number" value={form.quotation.roundOffAmount} onChange={(event) => handleQuotationChange("roundOffAmount", Number(event.target.value))} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Grand Total" type="number" value={summary.total.toFixed(2)} onChange={(event) => handleGrandTotalChange(Number(event.target.value))} />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Printable event details
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Family" value={form.quotation.headerConfig.event_host_family_name || ""} onChange={(event) => handleHeaderConfigChange("event_host_family_name", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label={eventParticipantLabels.primary} value={form.quotation.headerConfig.event_groom_name || ""} onChange={(event) => handleHeaderConfigChange("event_groom_name", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label={eventParticipantLabels.secondary} value={form.quotation.headerConfig.event_bride_name || ""} onChange={(event) => handleHeaderConfigChange("event_bride_name", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Event Date" type="date" InputLabelProps={{ shrink: true }} value={form.quotation.headerConfig.event_start_at || ""} onChange={(event) => handleHeaderConfigChange("event_start_at", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Venue" value={form.quotation.headerConfig.event_venue || ""} onChange={(event) => handleHeaderConfigChange("event_venue", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Occasion" value={form.quotation.headerConfig.event_occasion || ""} onChange={(event) => handleHeaderConfigChange("event_occasion", event.target.value)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Decor Type" value={form.quotation.headerConfig.event_decor_type_label || ""} onChange={(event) => handleHeaderConfigChange("event_decor_type_label", event.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label="Customer Message" value={form.quotation.customerMessage || ""} onChange={(event) => handleQuotationChange("customerMessage", event.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={3} label="Internal Notes" value={form.quotation.internalNotes || ""} onChange={(event) => handleQuotationChange("internalNotes", event.target.value)} />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Service sections
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button variant="outlined" startIcon={<AutoFixHighRoundedIcon />} onClick={rebuildSectionsFromSelectedEvent}>
                    Auto sections
                  </Button>
                  <Button startIcon={<AddRoundedIcon />} onClick={addSection}>
                    Add section
                  </Button>
                </Stack>
              </Stack>

              {form.quotation.eventId && mappedCategoryOptions.length ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Auto sections are linked to {selectedEventTypeLabel || "selected event type"}: {mappedCategoryOptions.map((option) => option.label).join(", ")}.
                </Alert>
              ) : null}

              {isSwitchingEvent ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Updating event details and service sections...
                </Alert>
              ) : null}

              {hasEventWithoutMappedCategories ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No service categories are linked to {selectedEventTypeLabel || "this event type"}. Add the Event Type in Service Categories, then use Auto sections.
                </Alert>
              ) : null}

              <Stack spacing={1.5}>
                {form.sections.map((section, sectionIndex) => (
                  <Accordion
                    key={`section-${sectionIndex}`}
                    expanded={expandedSection === sectionIndex}
                    onChange={(_, isExpanded) => setExpandedSection(isExpanded ? sectionIndex : false)}
                    TransitionProps={{ unmountOnExit: true }}
                    disableGutters
                    sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 252, 248, 0.92)" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreRoundedIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          minWidth: 0
                        }
                      }}
                    >
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} sx={{ width: "100%" }}>
                        <Typography sx={{ fontWeight: 700 }}>{section.sectionCode || `Section ${sectionIndex + 1}`}</Typography>
                        <Typography sx={{ color: "text.secondary" }}>{section.sectionTitle || "Untitled section"}</Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", ml: { sm: "auto" } }}>
                          {section.items.length} item{section.items.length === 1 ? "" : "s"}
                        </Typography>
                        <Button
                          color="error"
                          size="small"
                          startIcon={<DeleteOutlineRoundedIcon />}
                          onClick={(event) => {
                            event.stopPropagation();
                            removeSection(sectionIndex);
                          }}
                          onFocus={(event) => event.stopPropagation()}
                          sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </AccordionSummary>
                    {expandedSection === sectionIndex ? (
                      <AccordionDetails>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} md={3}>
                            <TextField fullWidth label="Section Code" value={section.sectionCode} error={Boolean(getFieldError(`sections.${sectionIndex}.sectionCode`))} helperText={getFieldError(`sections.${sectionIndex}.sectionCode`)} onChange={(event) => {
                              clearFieldError(`sections.${sectionIndex}.sectionCode`);
                              updateSection(sectionIndex, { ...section, sectionCode: event.target.value });
                            }} />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Service Category"
                              select
                              value={section.serviceCategoryId || ""}
                              onChange={(event) => handleSectionCategoryChange(sectionIndex, Number(event.target.value) || null)}
                            >
                              {visibleCategoryOptions.map((option) => (
                                <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} md={5}>
                            <TextField fullWidth label="Section Title" value={section.sectionTitle} error={Boolean(getFieldError(`sections.${sectionIndex}.sectionTitle`))} helperText={getFieldError(`sections.${sectionIndex}.sectionTitle`)} onChange={(event) => {
                              clearFieldError(`sections.${sectionIndex}.sectionTitle`);
                              updateSection(sectionIndex, { ...section, sectionTitle: event.target.value });
                            }} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth label="Section Description" value={section.sectionDescription || ""} onChange={(event) => updateSection(sectionIndex, { ...section, sectionDescription: event.target.value })} />
                          </Grid>
                        </Grid>
                        <Stack spacing={2}>
                          {section.items.map((item, itemIndex) => (
                            <Paper
                              key={`item-${sectionIndex}-${itemIndex}`}
                              variant="outlined"
                              sx={{
                                p: 2.2,
                                borderRadius: 3,
                                borderColor: item.catalogItemId ? "rgba(46, 125, 50, 0.5)" : "rgba(118, 93, 68, 0.18)",
                                background: item.catalogItemId ? "linear-gradient(180deg, rgba(237, 247, 237, 0.78), rgba(255, 252, 248, 0.96))" : "rgba(255, 252, 248, 0.9)",
                                boxShadow: item.catalogItemId ? "0 0 0 1px rgba(46, 125, 50, 0.08)" : "none"
                              }}
                            >
                              <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                  {item.catalogItemId ? (
                                    <Chip
                                      label="Item Master"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ mb: 1, fontWeight: 700, backgroundColor: "rgba(237, 247, 237, 0.95)" }}
                                    />
                                  ) : null}
                                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ xs: "stretch", md: "flex-start" }}>
                                    <Autocomplete
                                      fullWidth
                                      options={getCatalogOptionsForSection(section, item)}
                                      value={getCatalogOptionById(item.catalogItemId)}
                                      onChange={(_, option) => handleCatalogItemSelect(sectionIndex, itemIndex, option)}
                                      isOptionEqualToValue={(option, value) => Number(option.value) === Number(value.value)}
                                      getOptionLabel={(option) => option.label}
                                      sx={item.catalogItemId ? {
                                        "& .MuiOutlinedInput-root": {
                                          backgroundColor: "rgba(241, 248, 242, 0.72)",
                                          color: "text.primary",
                                          fontWeight: 500,
                                          "& fieldset": {
                                            borderColor: "rgba(76, 129, 83, 0.42)",
                                            borderWidth: 1
                                          },
                                          "&:hover fieldset": {
                                            borderColor: "rgba(76, 129, 83, 0.66)"
                                          },
                                          "&.Mui-focused fieldset": {
                                            borderColor: "rgba(76, 129, 83, 0.82)",
                                            borderWidth: 1
                                          },
                                          "& .MuiAutocomplete-endAdornment svg": {
                                            color: "rgba(76, 129, 83, 0.9)"
                                          }
                                        }
                                      } : undefined}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Catalog Item"
                                          placeholder="Search item master"
                                          color={item.catalogItemId ? "success" : "primary"}
                                        />
                                      )}
                                      noOptionsText="No real Item Master records found. Add Event Masters from Item Master or Categories."
                                    />
                                    <Button variant="outlined" onClick={() => openCatalogDialog(sectionIndex, itemIndex)} sx={{ minWidth: { md: 148 } }}>
                                      Create item
                                    </Button>
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Item Code" value={item.itemCode} error={Boolean(getFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemCode`))} helperText={getFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemCode`)} onChange={(event) => {
                                    clearFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemCode`);
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, itemCode: event.target.value };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                  <TextField fullWidth label="Item Name" value={item.itemName} error={Boolean(getFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemName`))} helperText={getFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemName`)} onChange={(event) => {
                                    clearFieldError(`sections.${sectionIndex}.items.${itemIndex}.itemName`);
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, itemName: event.target.value };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Quantity" type="number" value={item.quantity} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, quantity: Number(event.target.value) };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                  <TextField fullWidth label="Dimension / Size" value={item.dimensionText || ""} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, dimensionText: event.target.value };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Unit" value={item.unit || ""} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, unit: event.target.value };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Pricing Mode" select value={item.pricingMode} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, pricingMode: event.target.value as "manual" | "quantity_x_rate" };
                                    updateSection(sectionIndex, { ...section, items });
                                  }}>
                                    <MenuItem value="manual">Manual</MenuItem>
                                    <MenuItem value="quantity_x_rate">Qty x Rate</MenuItem>
                                  </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Rate" type="number" value={item.unitRate} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, unitRate: Number(event.target.value) };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Discount Type" select value={item.discountType} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, discountType: event.target.value as "none" | "flat" | "percent" };
                                    updateSection(sectionIndex, { ...section, items });
                                  }}>
                                    <MenuItem value="none">None</MenuItem>
                                    <MenuItem value="flat">Flat</MenuItem>
                                    <MenuItem value="percent">Percent</MenuItem>
                                  </TextField>
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Discount Value" type="number" value={item.discountValue} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, discountValue: Number(event.target.value) };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Tax Rate %" type="number" value={item.taxRate} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, taxRate: Number(event.target.value) };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField fullWidth label="Line Total" value={calculateItemLineTotal(item).toFixed(2)} disabled />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                  <TextField fullWidth multiline minRows={2} label="Description / Remarks" value={item.itemDescription || ""} onChange={(event) => {
                                    const items = [...section.items];
                                    items[itemIndex] = { ...item, itemDescription: event.target.value };
                                    updateSection(sectionIndex, { ...section, items });
                                  }} />
                                </Grid>
                                <Grid item xs={12}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                    <TextField fullWidth label="Include In Total" select value={item.includeInTotal ? "yes" : "no"} onChange={(event) => {
                                      const items = [...section.items];
                                      items[itemIndex] = { ...item, includeInTotal: event.target.value === "yes" };
                                      updateSection(sectionIndex, { ...section, items });
                                    }}>
                                      <MenuItem value="yes">Yes</MenuItem>
                                      <MenuItem value="no">No</MenuItem>
                                    </TextField>
                                    <TextField fullWidth label="Visible" select value={item.isVisible ? "yes" : "no"} onChange={(event) => {
                                      const items = [...section.items];
                                      items[itemIndex] = { ...item, isVisible: event.target.value === "yes" };
                                      updateSection(sectionIndex, { ...section, items });
                                    }}>
                                      <MenuItem value="yes">Yes</MenuItem>
                                      <MenuItem value="no">No</MenuItem>
                                    </TextField>
                                    <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => removeItem(sectionIndex, itemIndex)}>
                                      Remove item
                                    </Button>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <Button startIcon={<AddRoundedIcon />} onClick={() => addItem(sectionIndex)}>
                              Add item
                            </Button>
                          </Stack>
                        </Stack>
                      </AccordionDetails>
                    ) : null}
                  </Accordion>
                ))}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Notes, terms, milestones, and recipients
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Important notes
                    </Typography>
                    {form.notes.map((note, index) => (
                      <Paper key={`note-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                          <TextField fullWidth label="Note Title" value={note.title} onChange={(event) => {
                            const notes = [...form.notes];
                            notes[index] = { ...note, title: event.target.value };
                            setForm((current) => ({ ...current, notes }));
                          }} />
                          <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => requestRemove("Remove this note?", () => setForm((current) => ({
                            ...current,
                            notes: current.notes.filter((_, currentIndex) => currentIndex !== index)
                          })))}>
                            Remove
                          </Button>
                        </Stack>
                        {note.points.map((point, pointIndex) => (
                          <Stack key={pointIndex} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                            <TextField fullWidth multiline minRows={2} label={`Point ${pointIndex + 1}`} value={point.pointText} onChange={(event) => {
                              const notes = [...form.notes];
                              const points = [...notes[index].points];
                              points[pointIndex] = { ...point, pointText: event.target.value };
                              notes[index] = { ...note, points };
                              setForm((current) => ({ ...current, notes }));
                            }} />
                            <Button color="error" onClick={() => requestRemove("Remove this note point?", () => {
                              const notes = [...form.notes];
                              const points = notes[index].points.filter((_, currentIndex) => currentIndex !== pointIndex);
                              notes[index] = { ...note, points };
                              setForm((current) => ({ ...current, notes }));
                            })}>
                              Remove
                            </Button>
                          </Stack>
                        ))}
                        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => {
                          const notes = [...form.notes];
                          notes[index] = {
                            ...note,
                            points: [...note.points, { pointText: "", isHighlighted: false }]
                          };
                          setForm((current) => ({ ...current, notes }));
                        }}>
                          Add point
                        </Button>
                      </Paper>
                    ))}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button startIcon={<AddRoundedIcon />} onClick={() => setForm((current) => ({
                        ...current,
                        notes: [
                          ...current.notes,
                          {
                            title: "New Note",
                            icon: "",
                            isVisible: true,
                            points: [{ pointText: "", isHighlighted: false }]
                          }
                        ]
                      }))}>
                        Add note
                      </Button>
                      <Button variant="outlined" onClick={() => setForm((current) => ({ ...current, notes: createDefaultNotes() }))}>
                        Restore default notes
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Terms & payment milestones
                    </Typography>
                    {form.terms.map((term, index) => (
                      <Stack key={index} direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField fullWidth multiline minRows={2} label={`Term ${index + 1}`} value={term.termText} onChange={(event) => {
                          const terms = [...form.terms];
                          terms[index] = { ...term, termText: event.target.value };
                          setForm((current) => ({ ...current, terms }));
                        }} />
                        <Button color="error" onClick={() => requestRemove("Remove this term?", () => setForm((current) => ({
                          ...current,
                          terms: current.terms.filter((_, currentIndex) => currentIndex !== index)
                        })))}>
                          Remove
                        </Button>
                      </Stack>
                    ))}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button startIcon={<AddRoundedIcon />} onClick={() => setForm((current) => ({
                        ...current,
                        terms: [
                          ...current.terms,
                          {
                            termText: "",
                            isMandatory: true,
                            isVisible: true
                          }
                        ]
                      }))}>
                        Add term
                      </Button>
                      <Button variant="outlined" onClick={() => setForm((current) => ({ ...current, terms: createDefaultTerms() }))}>
                        Restore default terms
                      </Button>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    {form.paymentMilestones.map((milestone, index) => (
                      <Paper key={`milestone-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Milestone" value={milestone.milestoneLabel} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, milestoneLabel: event.target.value };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth label="Percent" type="number" value={milestone.percentage} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, percentage: Number(event.target.value) };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth label="Amount" type="number" value={milestone.amount || Number(((summary.total * milestone.percentage) / 100).toFixed(2))} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, amount: Number(event.target.value) };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Due Date" type="date" InputLabelProps={{ shrink: true }} value={milestone.dueDate || ""} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, dueDate: event.target.value };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Status" select value={milestone.paymentStatus} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, paymentStatus: event.target.value };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }}>
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="paid">Paid</MenuItem>
                              <MenuItem value="overdue">Overdue</MenuItem>
                              <MenuItem value="waived">Waived</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="Description" value={milestone.description || ""} onChange={(event) => {
                              const paymentMilestones = [...form.paymentMilestones];
                              paymentMilestones[index] = { ...milestone, description: event.target.value };
                              setForm((current) => ({ ...current, paymentMilestones }));
                            }} />
                          </Grid>
                          <Grid item xs={12}>
                            <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => requestRemove("Remove this milestone?", () => setForm((current) => ({
                              ...current,
                              paymentMilestones: current.paymentMilestones.filter((_, currentIndex) => currentIndex !== index)
                            })))}>
                              Remove milestone
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button startIcon={<AddRoundedIcon />} onClick={() => setForm((current) => ({
                        ...current,
                        paymentMilestones: [
                          ...current.paymentMilestones,
                          {
                            milestoneLabel: "New Milestone",
                            percentage: 0,
                            dueRuleText: "",
                            dueDate: "",
                            amount: 0,
                            description: "",
                            paymentStatus: "pending",
                            isVisible: true
                          }
                        ]
                      }))}>
                        Add milestone
                      </Button>
                      <Button variant="outlined" onClick={() => setForm((current) => ({ ...current, paymentMilestones: createDefaultPaymentMilestones() }))}>
                        Restore default milestones
                      </Button>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Recipients
                  </Typography>
                  <Stack spacing={1.5}>
                    {form.recipients.map((recipient, index) => (
                      <Paper key={`recipient-${index}`} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Contact" select value={recipient.customerContactId || ""} onChange={(event) => {
                              const recipients = [...form.recipients];
                              recipients[index] = { ...recipient, customerContactId: Number(event.target.value) };
                              setForm((current) => ({ ...current, recipients }));
                            }}>
                              {contactOptions.map((option) => (
                                <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Recipient Name" value={recipient.recipientName} onChange={(event) => {
                              const recipients = [...form.recipients];
                              recipients[index] = { ...recipient, recipientName: event.target.value };
                              setForm((current) => ({ ...current, recipients }));
                            }} />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Email" value={recipient.recipientEmail || ""} onChange={(event) => {
                              const recipients = [...form.recipients];
                              recipients[index] = { ...recipient, recipientEmail: event.target.value };
                              setForm((current) => ({ ...current, recipients }));
                            }} />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                    <Button startIcon={<AddRoundedIcon />} onClick={() => setForm((current) => ({
                      ...current,
                      recipients: [
                        ...current.recipients,
                        {
                          recipientName: "",
                          recipientEmail: "",
                          recipientPhone: "",
                          roleLabel: "Primary Customer",
                          isPrimary: current.recipients.length === 0,
                          canView: true,
                          canRespond: true
                        } as RecipientForm
                      ]
                    }))}>
                      Add recipient
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2.5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Print controls
              </Typography>
              <Stack spacing={1.5}>
                <TextField label="Show Item Price" select value={form.quotation.showItemPrice ? "yes" : "no"} onChange={(event) => handleQuotationChange("showItemPrice", event.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField label="Show Item Amount" select value={form.quotation.showItemAmount ? "yes" : "no"} onChange={(event) => handleQuotationChange("showItemAmount", event.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField label="Show Section Totals" select value={form.quotation.showSectionTotals ? "yes" : "no"} onChange={(event) => handleQuotationChange("showSectionTotals", event.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField fullWidth multiline minRows={3} label="Grand Total In Words" value={grandTotalWords} disabled />
                <TextField fullWidth multiline minRows={2} label="Total Note" value={form.quotation.totalNote || ""} onChange={(event) => handleQuotationChange("totalNote", event.target.value)} />
                <Divider />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Payment QR Code
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                  <Box
                    component="img"
                    src={resolveApiAssetUrl(form.quotation.footerConfig.payment_qr_image_url) || DEFAULT_PAYMENT_QR_IMAGE_URL}
                    alt="Payment QR preview"
                    onError={(event) => {
                      const defaultQrUrl = new URL(DEFAULT_PAYMENT_QR_IMAGE_URL, window.location.origin).href;

                      if (event.currentTarget.src !== defaultQrUrl) {
                        event.currentTarget.src = DEFAULT_PAYMENT_QR_IMAGE_URL;
                        setError("Uploaded QR image URL is not loading. Please upload the QR image again.");
                      }
                    }}
                    sx={{
                      width: 104,
                      height: 104,
                      objectFit: "contain",
                      borderRadius: 2,
                      border: "1px solid rgba(118, 93, 68, 0.18)",
                      background: "#fff",
                      p: 1
                    }}
                  />
                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Button variant="outlined" component="label">
                      Upload QR Image
                      <input type="file" accept="image/*" hidden onChange={handlePaymentQrUpload} />
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => handleFooterConfigChange("payment_qr_image_url", "")}
                    >
                      Use default QR
                    </Button>
                  </Stack>
                </Stack>
                <TextField
                  fullWidth
                  label="QR Title"
                  value={form.quotation.footerConfig.payment_qr_title || ""}
                  onChange={(event) => handleFooterConfigChange("payment_qr_title", event.target.value)}
                  placeholder="Scan to Pay"
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="QR Note"
                  value={form.quotation.footerConfig.payment_qr_note || ""}
                  onChange={(event) => handleFooterConfigChange("payment_qr_note", event.target.value)}
                  placeholder="Use this QR code for booking and milestone payments."
                />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Live totals
              </Typography>
              <Stack spacing={1.25}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography sx={{ fontWeight: 700 }}>₹ {summary.subtotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Adjustments</Typography>
                  <Typography sx={{ fontWeight: 700 }}>₹ {summary.adjustments.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Quote Discount</Typography>
                  <Typography sx={{ fontWeight: 700 }}>₹ {form.quotation.quoteDiscountTotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Round Off</Typography>
                  <Typography sx={{ fontWeight: 700 }}>₹ {form.quotation.roundOffAmount.toFixed(2)}</Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Grand Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>₹ {summary.total.toFixed(2)}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Box
        sx={{
          position: "fixed",
          left: { xs: 12, sm: "auto" },
          right: { xs: 12, md: 24 },
          bottom: { xs: 12, sm: 18 },
          zIndex: (theme) => theme.zIndex.appBar,
          pointerEvents: "none"
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          alignItems="stretch"
          sx={{ pointerEvents: "auto" }}
        >
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowUpRoundedIcon />}
            onClick={scrollToTop}
            sx={{
              minHeight: 44,
              px: 2.25,
              whiteSpace: "nowrap",
              background: "rgba(255, 250, 244, 0.96)",
              boxShadow: "0 12px 28px rgba(36, 24, 12, 0.16)"
            }}
          >
            Top
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            onClick={() => void saveQuotation()}
            disabled={saving || (isEditMode && loading)}
            sx={{ minHeight: 44, px: 3, whiteSpace: "nowrap", boxShadow: "0 12px 28px rgba(36, 24, 12, 0.2)" }}
          >
            {saving ? "Saving..." : "Save quotation"}
          </Button>
        </Stack>
      </Box>

      <Dialog open={catalogDialogOpen} onClose={closeCatalogDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Item Master</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Category"
                select
                value={catalogDraft.categoryId}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, categoryId: Number(event.target.value) || "" }))}
              >
                {visibleCategoryOptions.map((option) => (
                  <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Item Code"
                value={catalogDraft.itemCode}
                helperText="Generated automatically after save."
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Item Name"
                value={catalogDraft.itemName}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, itemName: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={catalogDraft.description}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, description: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Default Unit"
                value={catalogDraft.defaultUnit}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, defaultUnit: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Dimension Size"
                value={catalogDraft.defaultDimensionText}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, defaultDimensionText: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Default Rate"
                value={catalogDraft.defaultUnitRate}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, defaultUnitRate: event.target.value === "" ? "" : Number(event.target.value) }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pricing Mode"
                select
                value={catalogDraft.pricingMode}
                onChange={(event) => setCatalogDraft((current) => ({ ...current, pricingMode: event.target.value as "manual" | "quantity_x_rate" }))}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="quantity_x_rate">Quantity x Rate</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeCatalogDialog}>Cancel</Button>
          <Button variant="contained" onClick={() => void createCatalogItem()} disabled={creatingCatalogItem}>
            {creatingCatalogItem ? "Creating..." : "Create item"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingEventSwitch !== null} onClose={handleCancelEventSwitch} fullWidth maxWidth="xs">
        <DialogTitle>Change event?</DialogTitle>
        <DialogContent>
          <Typography>
            Changing the event will remove and rebuild the existing service sections and items for this quotation.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCancelEventSwitch}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleConfirmEventSwitch}>
            Change event
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmRemove)} onClose={() => setConfirmRemove(null)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm remove</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmRemove?.title || "Remove this item?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
